import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";
import { createModule, getModuleList } from "reduxStore/action/moduleActions";
import strings from "translate/language";
import { errorToast, successToast } from "utils/httpClient";
import { convertToSlug, handleListResponse, seo, isEmpty } from "utils/utils";
import Breadcrumb from "component/common/breadcrumb";
import TableDataNotFound from "component/TableDataNotFound";
import usePermission from "utils/usePermission";
import "./ModuleListing.css";

const EMPTY_MODULE_FORM = {
  module_title: "",
  parent_id: "",
  slug: "",
  sequence: "",
  module_id: "",
  update: false,
};

const selectPortalProps = {
  menuPortalTarget: typeof document !== "undefined" ? document.body : null,
  menuPosition: "fixed",
  styles: {
    menuPortal: (base) => ({ ...base, zIndex: 10060 }),
  },
};

const Module = () => {
  const [searchForm, setSearchForm] = useState({
    search_parent_id: "",
    search_name: "",
  });
  const [moduleForm, setModuleForm] = useState(EMPTY_MODULE_FORM);
  const [formVaild, setFormVaild] = useState(false);
  const [moduleData, setModuleData] = useState([]);
  const [parentName, setParentName] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [listFilter, setListFilter] = useState("");

  const dispatch = useDispatch();
  const modulesSelector = useSelector((state) => state.modules) || [];

  const { create, edit } = usePermission({
    create: "add_module",
    edit: "edit_module",
  });

  const parentOptions = useMemo(
    () => parentName.filter((item) => item.parent_id === null),
    [parentName]
  );

  const parentSelectOptions = useMemo(
    () =>
      parentOptions.map((item) => ({
        value: item._id,
        label: item.module_title,
      })),
    [parentOptions]
  );

  const modalParentOptions = useMemo(
    () =>
      parentSelectOptions.filter(
        (item) => !moduleForm.update || item.value !== moduleForm.module_id
      ),
    [parentSelectOptions, moduleForm.update, moduleForm.module_id]
  );

  const displayModules = useMemo(() => {
    const q = listFilter.trim().toLowerCase();
    const parents = moduleData
      .filter((item) => !item.parent_id)
      .sort((a, b) => (a.sequence || 0) - (b.sequence || 0));

    const childrenByParent = moduleData
      .filter((item) => item.parent_id)
      .reduce((acc, item) => {
        if (!acc[item.parent_id]) acc[item.parent_id] = [];
        acc[item.parent_id].push(item);
        return acc;
      }, {});

    Object.values(childrenByParent).forEach((children) =>
      children.sort((a, b) => (a.sequence || 0) - (b.sequence || 0))
    );

    const ordered = [];
    const usedChildIds = new Set();

    parents.forEach((parent) => {
      const children = childrenByParent[parent._id] || [];
      const parentMatches =
        !q || String(parent.module_title || "").toLowerCase().includes(q);
      const visibleChildren = q
        ? children.filter(
            (child) =>
              String(child.module_title || "")
                .toLowerCase()
                .includes(q) || parentMatches
          )
        : children;

      if (!parentMatches && !visibleChildren.length) return;

      ordered.push(parent);
      visibleChildren.forEach((child) => {
        ordered.push(child);
        usedChildIds.add(child._id);
      });
    });

    moduleData
      .filter((item) => item.parent_id && !usedChildIds.has(item._id))
      .sort((a, b) => (a.sequence || 0) - (b.sequence || 0))
      .forEach((orphan) => {
        if (
          !q ||
          String(orphan.module_title || "")
            .toLowerCase()
            .includes(q) ||
          String(orphan.parent_name || "")
            .toLowerCase()
            .includes(q)
        ) {
          ordered.push(orphan);
        }
      });

    return ordered;
  }, [moduleData, listFilter]);

  useEffect(() => {
    dispatch(getModuleList());
  }, []);

  useEffect(() => {
    if (modulesSelector?.create === true) {
      if (modulesSelector?.response?.status === 200) {
        setModuleForm(EMPTY_MODULE_FORM);
        setModalOpen(false);
        dispatch(getModuleList());
        successToast(modulesSelector?.response?.message);
      } else {
        errorToast(modulesSelector?.response?.message);
      }
      return;
    }
    if (modulesSelector?.response?.status === 200) {
      setModuleData(handleListResponse(modulesSelector?.response?.data));
      if (
        isEmpty(searchForm.search_parent_id) &&
        isEmpty(searchForm.search_name)
      ) {
        setParentName(handleListResponse(modulesSelector?.response?.data));
      }
    } else {
      setModuleData([]);
    }
  }, [modulesSelector]);

  useEffect(() => {
    seo({
      title: "Module Listing",
      metaDescription: "Module listing",
      canonicalLink: window.location.href,
    });
  }, []);

  const handleSearchInput = (e) => {
    setSearchForm({
      ...searchForm,
      [e.target.name]: e.target.value,
    });
  };

  const handleModuleInput = (e) => {
    const next = {
      ...moduleForm,
      [e.target.name]: e.target.value,
    };
    if (!moduleForm.update && e.target.name === "module_title") {
      next.slug = convertToSlug(e.target.value);
    }
    setModuleForm(next);
  };

  const handleParentChange = (selected) => {
    setModuleForm({
      ...moduleForm,
      parent_id: selected?.value || "",
    });
  };

  const handleSearchParentChange = (selected) => {
    setSearchForm({
      ...searchForm,
      search_parent_id: selected?.value || "",
    });
  };

  const filterDetail = () => {
    dispatch(
      getModuleList({
        search_parent_id: searchForm.search_parent_id,
        search_name: searchForm.search_name,
      })
    );
  };

  const filterReset = () => {
    setSearchForm({
      search_parent_id: "",
      search_name: "",
    });
    setListFilter("");
    dispatch(getModuleList());
  };

  const openAddModal = () => {
    setFormVaild(false);
    setModuleForm(EMPTY_MODULE_FORM);
    setModalOpen(true);
  };

  const openEditModal = (id = "") => {
    setFormVaild(false);
    const filtered = moduleData.filter((i) => i._id === id);
    if (!filtered.length) return;
    setModuleForm({
      module_id: id,
      module_title: filtered[0].module_title,
      parent_id: filtered[0].parent_id || "",
      slug: filtered[0].slug,
      sequence: filtered[0].sequence ?? "",
      update: true,
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setFormVaild(false);
    setModuleForm(EMPTY_MODULE_FORM);
  };

  const handleSubmit = () => {
    if (moduleForm.module_title === "") {
      setFormVaild(true);
      return;
    }
    setFormVaild(false);
    dispatch(createModule(moduleForm));
  };

  const selectedSearchParent =
    parentSelectOptions.find(
      (item) => item.value === searchForm.search_parent_id
    ) || null;

  const selectedModalParent =
    modalParentOptions.find((item) => item.value === moduleForm.parent_id) ||
    null;

  return (
    <div className="page-body justo-module-page">
      <Breadcrumb title={strings.module} titleactive={strings.moduleListing} />

      <div className="container-fluid justo-module">
        <div className="justo-module__card">
          <div className="justo-module__top">
            <div>
              <h2 className="justo-module__title">{strings.listOfModule}</h2>
              <p className="justo-module__subtitle">
                {moduleData.length} modules · scroll inside the table panel.
              </p>
            </div>
            {create ? (
              <div className="justo-module__top-actions">
                <button
                  type="button"
                  className="justo-module__btn justo-module__btn--solid"
                  onClick={openAddModal}
                >
                  <i className="fa fa-plus" aria-hidden="true" />
                  {strings.addNewModule}
                </button>
              </div>
            ) : null}
          </div>

          <div className="justo-module__filters">
            <div className="justo-module__filters-grid">
              <div className="justo-module__field">
                <label className="justo-module__label" htmlFor="search_name">
                  {strings.searchmoduleName}
                </label>
                <input
                  id="search_name"
                  className="justo-module__input"
                  type="text"
                  name="search_name"
                  value={searchForm.search_name}
                  onChange={handleSearchInput}
                  placeholder={strings.searchName}
                  autoComplete="off"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") filterDetail();
                  }}
                />
              </div>
              <div className="justo-module__field">
                <label className="justo-module__label">
                  {strings.searchparentOptional}
                </label>
                <Select
                  classNamePrefix="justo-module-select"
                  isSearchable
                  isClearable
                  placeholder={strings.selectParent}
                  options={parentSelectOptions}
                  value={selectedSearchParent}
                  onChange={handleSearchParentChange}
                  {...selectPortalProps}
                />
              </div>
              <div className="justo-module__field">
                <label className="justo-module__label" htmlFor="list_filter">
                  Filter visible
                </label>
                <input
                  id="list_filter"
                  className="justo-module__input"
                  type="text"
                  value={listFilter}
                  onChange={(e) => setListFilter(e.target.value)}
                  placeholder="Type to narrow list"
                  autoComplete="off"
                />
              </div>
              <div className="justo-module__filter-actions">
                <button
                  type="button"
                  className="justo-module__btn justo-module__btn--solid"
                  onClick={filterDetail}
                >
                  <i className="fa fa-search" aria-hidden="true" />
                  {strings.search}
                </button>
                <button
                  type="button"
                  className="justo-module__btn justo-module__btn--ghost"
                  onClick={filterReset}
                >
                  {strings.reset}
                </button>
              </div>
            </div>
          </div>

          <div className="justo-module__scroll">
            <table className="justo-module__table">
              <thead>
                <tr>
                  <th scope="col">{strings.modules}</th>
                  <th scope="col">{strings.parent}</th>
                  <th className="justo-module__col-seq" scope="col">
                    {strings.sequence}
                  </th>
                  {edit ? (
                    <th className="justo-module__col-action" scope="col">
                      {strings.action}
                    </th>
                  ) : null}
                </tr>
              </thead>
              <tbody>
                {displayModules.map((item) => {
                  const isChild = !!item.parent_id;
                  return (
                    <tr
                      key={item._id}
                      className={!isChild ? "justo-module__row--parent" : ""}
                    >
                      <td>
                        <div
                          className={`justo-module__module-cell${
                            isChild ? " justo-module__module-cell--child" : ""
                          }`}
                        >
                          <span
                            className={`justo-module__badge${
                              isChild ? " justo-module__badge--child" : ""
                            }`}
                            aria-hidden="true"
                          >
                            {isChild ? "C" : "P"}
                          </span>
                          <span
                            className={`justo-module__name${
                              isChild ? " justo-module__name--child" : ""
                            }`}
                          >
                            {item.module_title}
                          </span>
                        </div>
                      </td>
                      <td className="justo-module__parent">
                        {item.parent_name || "—"}
                      </td>
                      <td className="justo-module__col-seq">
                        <span className="justo-module__seq">
                          {item.sequence ?? "—"}
                        </span>
                      </td>
                      {edit ? (
                        <td className="justo-module__col-action">
                          <div className="justo-module__actions">
                            <button
                              type="button"
                              className="justo-module__btn justo-module__btn--outline justo-module__btn--sm"
                              onClick={() => openEditModal(item._id)}
                            >
                              <i className="fa fa-pencil" aria-hidden="true" />
                              {strings.edit}
                            </button>
                          </div>
                        </td>
                      ) : null}
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <TableDataNotFound data={displayModules} />
          </div>
        </div>
      </div>

      {modalOpen ? (
        <div
          className="justo-module__modal-backdrop"
          role="presentation"
          onClick={closeModal}
        >
          <div
            className="justo-module__modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="module-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="justo-module__modal-head">
              <div>
                <h3 className="justo-module__modal-title" id="module-modal-title">
                  {moduleForm.update ? strings.edit : strings.addNewModule}
                </h3>
                <p className="justo-module__modal-subtitle">
                  {moduleForm.update
                    ? "Update module details and sequence."
                    : "Create a parent or child module for navigation."}
                </p>
              </div>
              <button
                type="button"
                className="justo-module__modal-close"
                aria-label={strings.cancel}
                onClick={closeModal}
              >
                ×
              </button>
            </div>

            <div className="justo-module__modal-body">
              <div className="justo-module__modal-grid">
                <div className="justo-module__modal-field justo-module__modal-field--full">
                  <label className="justo-module__modal-label">
                    {strings.parentOptional}
                  </label>
                  <Select
                    classNamePrefix="justo-module-select"
                    isSearchable
                    isClearable
                    placeholder={strings.selectParent}
                    options={modalParentOptions}
                    value={selectedModalParent}
                    onChange={handleParentChange}
                    noOptionsMessage={() => "No parent modules found"}
                    {...selectPortalProps}
                  />
                </div>

                <div className="justo-module__modal-field justo-module__modal-field--full">
                  <label
                    className="justo-module__modal-label"
                    htmlFor="module_title"
                  >
                    {strings.moduleName}
                  </label>
                  <input
                    id="module_title"
                    className="justo-module__modal-input"
                    type="text"
                    autoComplete="off"
                    placeholder={strings.moduleName}
                    name="module_title"
                    value={moduleForm.module_title}
                    onChange={handleModuleInput}
                  />
                  {formVaild ? (
                    <span className="justo-module__modal-error">
                      {strings.modulenameisrequired}
                    </span>
                  ) : null}
                </div>

                <div className="justo-module__modal-field">
                  <label className="justo-module__modal-label" htmlFor="sequence">
                    {strings.sequence}
                  </label>
                  <input
                    id="sequence"
                    className="justo-module__modal-input"
                    type="number"
                    autoComplete="off"
                    placeholder={strings.sequence}
                    name="sequence"
                    value={moduleForm.sequence}
                    onChange={handleModuleInput}
                  />
                </div>

                <div className="justo-module__modal-field">
                  <label className="justo-module__modal-label" htmlFor="slug">
                    {strings.slug}
                  </label>
                  <input
                    id="slug"
                    className="justo-module__modal-input"
                    type="text"
                    name="slug"
                    value={moduleForm.slug}
                    readOnly
                  />
                </div>
              </div>
            </div>

            <div className="justo-module__modal-foot">
              <button
                type="button"
                className="justo-module__btn justo-module__btn--ghost"
                onClick={closeModal}
              >
                {strings.cancel}
              </button>
              <button
                type="button"
                className="justo-module__btn justo-module__btn--solid"
                onClick={handleSubmit}
              >
                <i className="fa fa-check" aria-hidden="true" />
                {moduleForm.update ? strings.update : strings.submit}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default Module;
